<template>
  <div class="products">
    <div
      v-if="result"
      class="products-list-loader"
    >
      <div class="category-products">
        <div v-if="shouldLoad && loadDirection === -1">
          <loading />
        </div>
        <div class="products-list">
          <CustomProductCard
            v-for="(product, index) in products"
            :key="product.uid"
            class="product desktop-product"
            :page-number="loadedPage"
            :show-page-up-trigger="
              (index + 1) % Math.round(itemsPerPage / 2) === 0 &&
                (index + 1) % itemsPerPage !== 0
            "
            :show-page-down-trigger="(index + 1) % itemsPerPage === 1"
            image-loading="eager"
            :is-big-image="isBigImage(index)"
            :card-index="index"
            :image="
              getRolloverImage(product, isBigImage(index) ? 1000 : 500, 'a')
                ? [
                  getProductThumbnailImage(
                    product,
                    isBigImage(index) ? 1000 : 500,
                    'a'
                  ),
                  getRolloverImage(
                    product,
                    isBigImage(index) ? 1000 : 500,
                    'a'
                  ),
                ]
                : getProductThumbnailImage(
                  product,
                  isBigImage(index) ? 1000 : 500,
                  'a'
                )
            "
            :link="localePath(buildProductUrl(product))"
            :colors="
              getSfColorsFromAttributes(getProductColorAttribute(product))
            "
            :title="getProductName(product)"
            :image-alt="getProductImageAlt(product, 'thumbnail')"
            :regular-price="$n(getProductPrice(product).regular, 'currency')"
            :special-price="
              getProductPrice(product).special
                ? $n(getProductPrice(product).special, 'currency')
                : null
            "
            :omnibus-price="product.omnibus_price ? $n(product.omnibus_price,'currency') : null"
            :is-in-wishlist="!!itemsInWishlist[product.sku]"
            :is-added-to-cart="true"
            :add-to-cart-disabled="false"
            :sku="getProductMerchantSku(product)"
            :category="product.categories[0].name"
            :full-price-gtm="getPriceFixedDecimals(getProductPrice(product).regular)"
            :price-gtm="getPriceFixedDecimals(getProductPrice(product).special) || getPriceFixedDecimals(getProductPrice(product).regular)"
            @click:wishlist="toggleWishlist(product)"
          />
        </div>
        <div
          id="scroll-trigger-down"
          ref="infinitescrolltriggerdown"
        />
        <div v-if="shouldLoad && loadDirection === 1">
          <loading />
        </div>
      </div>
    </div>
    <div
      v-else
      class="not-product"
    >
      <div class="container">
        <h1>{{ $t('No products were found') }}</h1>
      </div>
    </div>
    <CategoryGtm :products-data="products" />
  </div>
</template>

<script>
import { productGetters, facetGetters, useFacet } from '@vue-storefront/gemini';
import {
  defineComponent, onMounted, toRef, useContext, useFetch, watch,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@gemini/vsf-cache';
import productPlaceholder from '~/assets/images/product_placeholder.svg';
import { useUrlHelpers } from '~/composables';
import {
  getSfColorsFromAttributes,
  getProductColorAttribute,
  getPriceFixedDecimals,
} from '~/helpers/product/productData';
import useWishlist from '~/composables/useWishlist';
import CustomProductCard from '~/components/CustomProductCard';
import Loading from '~/components/loading';
import CategoryGtm from '~/components/Gtm/CategoryGtm';

export default defineComponent({
  name: 'ProductList',
  components: {
    CategoryGtm,
    CustomProductCard,
    Loading,
  },
  props: {
    searchSpecsStr: {
      type: String,
      required: true,
      default() {
        return '';
      },
    },
    products: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
    shouldLoad: {
      required: false,
      type: Boolean,
      default: false,
    },
    loadDirection: {
      required: false,
      type: Number,
      default: 1,
    },
    itemsPerPage: {
      required: false,
      type: Number,
      default: 14,
    },
    page: {
      required: false,
      type: Number,
      default: 1,
    },
    loadedPage: {
      required: false,
      type: Number,
      default: 1,
    },
  },
  setup(props) {
    const { error: nuxtError } = useContext();
    const { buildProductUrl } = useUrlHelpers();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const { result } = useFacet(props.searchSpecsStr);

    const productsRef = toRef(props, 'products');
    const { itemsInWishlist, toggleWishlist, loadItemsFromList } = useWishlist(productsRef);

    const getProductId = (grn) => {
      if (typeof grn === 'string' || grn instanceof String) {
        const tmp = grn.split('::');
        if (tmp.length >= 2) {
          return tmp[1];
        }
        return tmp[0];
      }
      return null;
    };

    onMounted(async () => {
      await loadItemsFromList();
    });

    watch(productsRef, async (newProducts, oldProducts) => {
      const productsDifference = newProducts.filter((x) => !oldProducts.includes(x));
      if (productsDifference.length > 0) {
        await loadItemsFromList(productsDifference);
      }
    }, { deep: true });

    const { addTags } = useCache();

    useFetch(() => {
      if (!result.value || props.products.length === 0) {
        nuxtError({ statusCode: 404 });
      }
      const tags = [];
      const skus = facetGetters.getProducts(result.value).reduce(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        (acc, product) => [...acc, productGetters.getProductSku(product)],
        [],
      );
      skus.forEach((grn) => {
        const id = getProductId(grn);
        if (id != null) {
          tags.push({ prefix: CacheTagPrefix.Product, value: id });
        }
      });

      if (skus.length > 0) {
        tags.push(
          { prefix: CacheTagPrefix.Category, value: 'productlist' },
          {
            prefix: CacheTagPrefix.Product,
            value: skus,
          },
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        addTags(tags);
      }
    });

    return {
      result,
      buildProductUrl,
      getSfColorsFromAttributes,
      getProductColorAttribute,
      getPriceFixedDecimals,
      getProducts: facetGetters.getProducts,
      getRolloverImage: productGetters.getRolloverImage,
      getProductThumbnailImage: productGetters.getProductThumbnailImage,
      getProductName: productGetters.getName,
      getProductPrice: productGetters.getPrice,
      getProductImageAlt: productGetters.getProductImageAlt,
      getProductMerchantSku: productGetters.getProductMerchantSku,
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      isBigImage: (index) => ((index + 1) % 14) === 3 || ((index + 1) % 14) === 10,
      itemsInWishlist,
      toggleWishlist,
    };
  },

  data() {
    return {
      productPlaceholder,
      direction: 0,
      startY: 0,
    };
  },
  watch: {
    products: {
      handler() {
        this.$nextTick(() => {
          this.scrollPageTrigger();
        });
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollTrigger();
      this.scrollPageTrigger();
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    onScrollUpLoad() {
      this.$emit('loadMoreUp');
    },
    onScrollDownLoad() {
      this.$emit('loadMore');
    },
    onPageDown(pageNumber) {
      this.$emit('pageDown', pageNumber);
    },
    onPageUp(pageNumber) {
      this.$emit('pageUp', pageNumber);
    },
    scrollTrigger() {
      const observer = new IntersectionObserver((entries) => {
        if (!Array.isArray(entries)) {
          return;
        }

        entries.forEach((entry) => {
          if (
            entry.target.id === 'scroll-trigger-down'
            && entry.intersectionRatio
            && window.scroll
            && window.scrollY > 0
          ) {
            this.onScrollDownLoad();
          }
        });
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      observer.observe(this.$refs.infinitescrolltriggerdown);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      /* observer.observe(this.$refs.infinitescrolltriggerup); */
    },
    scrollPageTrigger() {
      const observer = new IntersectionObserver((entries) => {
        if (!Array.isArray(entries)) {
          return;
        }

        entries.forEach((entry) => {
          if (entry.intersectionRatio && window.scroll && window.scrollY > 0) {
            if (
              entry.target.classList.contains('page-up')
              && this.direction === -1
            ) {
              const pageToShow = entry.target.id.split('-').at(-1);
              this.onPageUp({
                page: pageToShow,
                direction: this.direction,
              });
            }
            if (
              entry.target.classList.contains('page-down')
              && this.direction === 1
            ) {
              const pageToShow = entry.target.id.split('-').at(-1);
              this.onPageDown({
                page: pageToShow,
                direction: this.direction,
              });
            }
          }
        });
      });

      const triggerPageUp = `.page-up-${this.loadedPage}`;
      const elTriggerPageUp = this.$el.querySelector(triggerPageUp);
      if (elTriggerPageUp) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        observer.observe(elTriggerPageUp);
      }

      const triggerPageDown = `.page-down-${this.loadedPage}`;
      const elTriggerPageDown = this.$el.querySelector(triggerPageDown);
      if (elTriggerPageDown) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        observer.observe(elTriggerPageDown);
      }
    },
    handleScroll() {
      const { scrollY } = window;
      this.direction = scrollY > this.startY ? 1 : -1;
      this.startY = scrollY;
      if (scrollY === 0 && this.direction === -1) {
        this.onScrollUpLoad();
      }
    },
  },
});
</script>

<style lang='scss'>
.products-list {
  .sf-product-card {
    height: 100%;

    .sf-product-card__image-wrapper {
      height: 100%;
      display: flex;
      flex-direction: column;

      .sf-image--wrapper {
        height: 100%;
      }

      .sf-product-card__link {
        text-align: center;
        flex-grow: 1;
      }
    }
  }

  .yf-custom-product-card {
    .sf-product-card__title {
      margin: 5px auto 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
      @media (max-width: 768px) {
        margin: 1px auto 0;
      }
    }

    .sf-product-card__title:hover {
      -webkit-line-clamp: 3;
    }

    .yf-product-content {
      text-align: center;
      padding-bottom: 5px;
    }

    .sf-product-card__price {
      margin-top: 0;
      @media (max-width: 768px) {
        margin-top: 1px;
      }
    }

    .sf-product-card__colors-badge {
      width: 12px;
      height: 12px;
    }
  }
}

.product.yf-custom-product-card .sf-product-card__title {
  margin: -13px 0 0;
  color: #0b0e0c;
  font-family: 'Frank Ruhl Libre', serif;
  font-weight: 400;
  white-space: pre-line;
}

.sf-image--placeholder {
  padding: 8vh;
}

@media (min-width: 1024px) {
  .product:nth-child(14n + 3),
  .product:nth-child(14n + 10) {
    &.yf-custom-product-card {
      .sf-product-card__link {
        .sf-image--wrapper.sf-product-card__image {
          height: 100%;
        }
      }
    }

    &.yf-custom-product-card .sf-product-card__title {
      text-align: center;
      max-width: unset;
    }

    &.yf-custom-product-card .sf-product-card .sf-price__regular {
      font-size: 12px;
    }
  }
}

@media (max-width: 1023px) {
  .product.yf-custom-product-card .sf-product-card__title {
    line-height: 16px;
  }

  .product:nth-child(5n + 3) {
    &.yf-custom-product-card .sf-product-card__title {
      line-height: normal;
      max-width: unset;
    }

    &.yf-custom-product-card .sf-product-card .sf-price__regular {
      font-size: 12px;
    }
  }

  .products-list {
    .yf-custom-product-card .sf-overlay {
      background: transparent;
    }

    .sf-color-picker__label {
      display: none;
    }

    .yf-custom-product-card .yf-product-content {
      opacity: 1;
      visibility: visible;
    }
  }
}
</style>

<style lang='scss' scoped>
.circle-loader-category-container {
  position: relative;
  height: 50vh;
  @include for-mobile {
    height: 70vh;
  }

  .circle-loader-category {
    width: 50px;
    height: 50px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    border: 5px solid #fff;
    border-top: 5px solid gray;
    animation: loader 1.5s infinite linear;
  }
}

@keyframes loader {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

#category {
  .product-list-loader {
    min-height: 100vh;

    .sf-loader {
      &__overlay {
        position: absolute;
        min-height: 50vh;
      }
    }
  }
}

.products {
  background: #f6f3ef;
  padding: 38px 0 95px;
  position: relative;

  #scroll-loader {
    height: 50px;
  }

  .circle-loader-up {
    position: relative;
    width: 50px;
    height: 50px;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    border: 5px solid #fff;
    border-top: 5px solid gray;
    animation: loader 1.5s infinite linear;
  }

  .circle-loader {
    width: 50px;
    height: 50px;
    position: absolute;
    bottom: 0%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    border: 5px solid #fff;
    border-top: 5px solid gray;
    animation: loader 1.5s infinite linear;
  }
}

@keyframes loader {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.products-list {
  display: grid;
}

.category-products {
  margin: 0 auto;
  max-width: 1246px;

  a {
    text-decoration-line: none;
  }

  .product-wish {
    opacity: 0;
    position: absolute;
    right: 20px;
    top: 20px;
  }

  p {
    margin: 0 0 10px;

    &:last-child {
      margin: 0;
    }
  }

  .product-content {
    position: relative;
    height: 100%;

    .product-content-image,
    figure {
      height: 100%;
    }

    .product-content-info {
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      align-items: center;
      background: #fff;
      bottom: 0;
      display: -webkit-box;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: center;
      left: 0;
      line-clamp: 2;
      min-height: 100px;
      opacity: 0;
      overflow: hidden;
      padding: 15px 0 15px;
      position: absolute;
      text-align: center;
      text-overflow: ellipsis;
      transition: opacity 0.7s;
      white-space: normal;
      width: 100%;

      .product-info-name {
        max-width: 200px;

        line-height: 24px;

        a {
          color: #0b0e0c;
          display: inline-block;
          font-family: 'Frank Ruhl Libre';
          font-size: 20px;
          font-weight: 400;
          line-height: 24px;
          padding: 0;
        }
      }

      .product-info-price {
        span {
          color: #0b0e0c;
          font-family: 'Spartan';
          font-size: 12px;
          font-weight: 500;
          text-align: center;
        }
      }

      .product-info-color .sf-color-picker {
        position: static;

        button {
          height: 24px;
          margin: 5px;
          width: 24px;
        }
      }
    }

    &:hover {
      .product-content-info,
      .product-wish {
        opacity: 1;
      }
    }
  }

  figure {
    display: flex;
    margin: 0;
  }

  img {
    width: 100%;
  }
}

@media (max-width: 1023px) {
  .category-products .product-content .product-content-info .product-info-name {
    max-width: 200px;
  }
  .products {
    padding: 0 24px;
  }

  .product-info-color {
    display: none;
  }

  .products-list {
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;

    .product:nth-child(5n + 3) {
      grid-row: auto / span 2;
      grid-column: auto / span 2;

      .product-content .product-content-info {
        padding: 15px 0 15px;

        .product-info-name {
          max-width: unset;

          a {
            font-size: 26px;
            line-height: 16px;
          }
        }
      }
    }
  }

  .category-products {
    padding-bottom: 84px;

    .product-content {
      .product-content-info {
        opacity: 1;

        .product-info-name {
          padding: 0;

          a {
            font-size: 16px;
            line-height: 16px;
          }
        }

        .product-info-price {
          font-size: 15px;
        }

        .product-info-color {
          .sf-color-picker button {
            width: 20px;
            height: 20px;
          }
        }
      }
    }

    .product-wish {
      display: none;
    }
  }
}

@media (max-width: 640px) {
  .category-products .product-content .product-content-info {
    padding: 10px 0;

    .product-info-name {
      line-height: 20px;
    }
  }

  .products-list,
  .product:nth-child(5n + 3),
  .product-content,
  .product-content-info,
  .product-info-name,
  a {
    font-size: 20px;
  }
}

@media (min-width: 1024px) {
  // .category-products
  //   .product-content
  //   .product-content-info
  //   .product-info-name
  //   a {
  //   overflow: hidden;
  //   text-overflow: ellipsis;
  //   white-space: nowrap;
  //   width: 200px;
  //   &:hover {
  //     overflow: visible;
  //     white-space: initial;
  //   }
  // }

  .products-list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 24px;
    grid-template-rows: repeat(auto, auto);

    .product {
      &:nth-child(14n + 3) {
        grid-row: auto / span 2;
        grid-column: 3 / span 2;

        .yf-custom-product-card .sf-product-card__title {
          font-weight: 400;
          line-height: 25px;
          text-align: center;
          margin-top: 5px;
          margin-bottom: 0;
        }

        &.big-image {
          .product-content .product-content-info {
            padding: 20px 0 20px;
            @include for-mobile {
              padding: 15px 0 15px;
            }

            .product-info-name {
              max-width: unset;

              a {
                font-size: 30px;
                color: #222a24;
                @include for-mobile {
                  font-size: 16px;
                  line-height: 16px;
                }
              }
            }
          }
        }

        &.small-image {
          grid-row: auto;
          grid-column: auto;
        }
      }

      &:nth-child(14n + 10) {
        grid-row: auto / span 2;
        grid-column: 1 / span 2;

        .product-content .product-content-info {
          padding: 20px 0 20px;
          @include for-mobile {
            padding: 15px 0 15px;
          }

          .product-info-name {
            max-width: unset;

            a {
              font-size: 30px;
              color: #222a24;
              @include for-mobile {
                font-size: 16px;
                line-height: 16px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
